import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patentApi, Patent, UpdatePatentDto } from 'apis/patent';
import Table, { Column } from 'ui/Table';
import FilterCard, { FilterCardGroup } from 'ui/FilterCard';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import { toast } from 'react-toastify';

type Filter = 'ALL' | 'VISIBLE' | 'HIDDEN';

const PatentVisibilityManager: React.FC = () => {
    const navigate = useNavigate();
    const [list, setList] = useState<Patent[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');

    useEffect(() => { fetchList(); }, []);

    const fetchList = async () => {
        try {
            const res = await patentApi.getPatents({ limit: 1000 });
            setList(res.data.data);
        } catch {
            toast.error('특허를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (row: Patent) => {
        try {
            const next = !row.is_visible;
            await patentApi.updatePatent(row.id, { is_visible: next } as UpdatePatentDto);
            setList(prev => prev.map(p => p.id === row.id ? { ...p, is_visible: next } : p));
            toast.success(next ? '홈페이지에 노출됩니다.' : '홈페이지에서 숨김 처리되었습니다.');
        } catch {
            toast.error('변경에 실패했습니다.');
        }
    };

    const formatDate = (d?: string) => {
        if (!d) return '-';
        const date = new Date(d);
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    };

    const visibleCount = list.filter(p => p.is_visible).length;
    let filtered = list;
    if (filter === 'VISIBLE') filtered = list.filter(p => p.is_visible);
    if (filter === 'HIDDEN') filtered = list.filter(p => !p.is_visible);

    const columns: Column<Patent>[] = [
        {
            key: 'is_visible',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggle(row)}
                    aria-label="홈페이지 노출"
                />
            ),
        },
        { key: 'patent_number', header: '특허번호', width: '150px', align: 'center', render: (v) => <span className="tabular-nums">{v}</span> },
        { key: 'invention_title', header: '발명의 명칭', render: (v) => <span className="font-medium">{v}</span> },
        { key: 'registration_date', header: '등록일', width: '120px', align: 'center', render: (v) => <span className="tabular-nums text-muted-foreground">{formatDate(v)}</span> },
        {
            key: 'certificate_url',
            header: '특허증',
            width: '70px',
            align: 'center',
            render: (v) => v ? (
                <img src={v} alt="특허증" className="w-9 h-9 rounded object-cover mx-auto border border-border" />
            ) : <span className="text-xs text-neutral-400">-</span>,
        },
    ];

    return (
        <div>
            <FilterCardGroup columns={3}>
                <FilterCard label="전체 특허" count={loading ? undefined : list.length} suffix="건" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
                <FilterCard label="홈페이지 노출중" count={loading ? undefined : visibleCount} suffix="건" active={filter === 'VISIBLE'} onClick={() => setFilter('VISIBLE')} />
                <FilterCard label="비노출" count={loading ? undefined : list.length - visibleCount} suffix="건" active={filter === 'HIDDEN'} onClick={() => setFilter('HIDDEN')} />
            </FilterCardGroup>

            <Section
                className="overflow-hidden"
                title={
                    <>
                        특허 목록
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{filtered.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 회사소개 > 특허 및 상훈에 노출될 특허를 큐레이션합니다. 특허 자체의 정보 수정은 특허 관리에서 가능합니다."
                flush
            >
                {loading ? (
                    <Loading text="특허를 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={filtered}
                        pagination
                        pageSize={15}
                        emptyMessage="해당하는 특허가 없습니다."
                        onRowClick={(row) => navigate(`/admin/patent?edit=${row.id}`)}
                    />
                )}
            </Section>
        </div>
    );
};

export default PatentVisibilityManager;
